import { useState } from "react";
import { X, Eye, EyeOff, Package, MapPin, CreditCard, AlertCircle, CheckCircle2, Sparkles, Gift, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { z } from "zod";

interface AuthDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const emailSchema = z.string().trim().email("Zadajte platný e-mail");
const passwordSchema = z.string().min(6, "Heslo musí mať aspoň 6 znakov");

export const AuthDrawer = ({ isOpen, onClose }: AuthDrawerProps) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; firstName?: string; lastName?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();

  const benefits = [
    {
      icon: Package,
      title: "Sledovanie objednávok",
      description: "Prehľad histórie a stavu všetkých objednávok",
    },
    {
      icon: CreditCard,
      title: "Rýchlejší nákup",
      description: "Uložené fakturačné údaje = nákup na 2 kliky",
    },
    {
      icon: MapPin,
      title: "Viac adries doručenia",
      description: "Uložte si domov, prácu aj iné adresy",
    },
    {
      icon: Gift,
      title: "Exkluzívne zľavy",
      description: "Prístup k zľavám a akciám len pre členov",
    },
  ];

  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      newErrors.email = emailResult.error.errors[0].message;
    }
    
    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) {
      newErrors.password = passwordResult.error.errors[0].message;
    }

    if (!isLogin) {
      if (!firstName.trim()) newErrors.firstName = "Zadajte meno";
      if (!lastName.trim()) newErrors.lastName = "Zadajte priezvisko";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock login check using AuthContext
    if (isLogin) {
      const success = login(email, password);
      if (success) {
        toast.success("Prihlásenie úspešné", {
          description: "Vitajte späť!",
        });
        resetForm();
        onClose();
      } else {
        toast.error("Nesprávne prihlasovacie údaje", {
          description: "Použite: demo@merch.sk / demo123",
        });
      }
    } else {
      toast.success("Registrácia úspešná", {
        description: "Váš účet bol vytvorený. Prihláste sa pomocou demo údajov.",
      });
      setIsLogin(true);
    }
    
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setErrors({});
  };

  const handleModeSwitch = () => {
    setIsLogin(!isLogin);
    setErrors({});
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-[200] animate-fade-in"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-background z-[201] shadow-2xl animate-slide-in-right overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">
                {isLogin ? "Vitajte späť" : "Vytvorte si účet"}
              </h2>
              <p className="text-xs text-muted-foreground">
                {isLogin ? "Prihláste sa do svojho účtu" : "Registrácia trvá len chvíľu"}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Demo credentials hint - only on login */}
          {isLogin && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
              <p className="text-sm font-medium text-foreground mb-1">Demo prihlásenie:</p>
              <p className="text-xs text-muted-foreground font-mono">
                E-mail: demo@merch.sk<br />
                Heslo: demo123
              </p>
            </div>
          )}

          {/* Benefits - Only show on register */}
          {!isLogin && (
            <div className="bg-gradient-to-br from-primary/10 to-accent/20 rounded-2xl p-5 border border-primary/20">
              <h3 className="text-base font-bold text-foreground flex items-center gap-2 mb-4">
                <Gift className="w-5 h-5 text-primary" />
                Výhody registrácie
              </h3>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-background rounded-xl border border-border"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{benefit.title}</p>
                      <p className="text-xs text-muted-foreground">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name fields - only for register */}
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">Meno</label>
                  <Input
                    type="text"
                    placeholder="Váš meno"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (errors.firstName) setErrors(prev => ({ ...prev, firstName: undefined }));
                    }}
                    className={`h-12 rounded-xl px-4 ${errors.firstName ? "border-destructive" : ""}`}
                  />
                  {errors.firstName && (
                    <p className="text-[10px] text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">Priezvisko</label>
                  <Input
                    type="text"
                    placeholder="Vaše priezvisko"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (errors.lastName) setErrors(prev => ({ ...prev, lastName: undefined }));
                    }}
                    className={`h-12 rounded-xl px-4 ${errors.lastName ? "border-destructive" : ""}`}
                  />
                  {errors.lastName && (
                    <p className="text-[10px] text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Email */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">E-mail</label>
              <div className="relative">
                <Input
                  type="email"
                  placeholder="vas@email.sk"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors(prev => ({ ...prev, email: undefined }));
                  }}
                  className={`h-12 rounded-xl px-4 pr-10 ${
                    errors.email 
                      ? "border-destructive focus-visible:ring-destructive" 
                      : email && !errors.email 
                        ? "border-primary/50" 
                        : ""
                  }`}
                />
                {email && !errors.email && (
                  <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                )}
              </div>
              {errors.email && (
                <p className="text-[10px] text-destructive flex items-center gap-1 pl-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Heslo</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors(prev => ({ ...prev, password: undefined }));
                  }}
                  className={`h-12 rounded-xl px-4 pr-12 ${
                    errors.password 
                      ? "border-destructive focus-visible:ring-destructive" 
                      : password.length >= 6 
                        ? "border-primary/50" 
                        : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-[10px] text-destructive flex items-center gap-1 pl-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.password}
                </p>
              )}
            </div>

            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Zabudli ste heslo?
                </button>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full h-12 rounded-xl text-base font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/25 active:scale-[0.98]"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Spracovávam...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isLogin ? "Prihlásiť sa" : "Vytvoriť účet"}
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </span>
              )}
            </Button>
          </form>


          {/* Switch mode */}
          <div className="text-center pt-4 border-t border-border">
            <span className="text-sm text-muted-foreground">
              {isLogin ? "Nemáte účet? " : "Máte už účet? "}
            </span>
            <button
              type="button"
              onClick={handleModeSwitch}
              className="text-sm font-semibold text-primary hover:underline underline-offset-2"
            >
              {isLogin ? "Zaregistrujte sa" : "Prihláste sa"}
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 pt-4">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Truck className="w-4 h-4" />
              <span className="text-[10px]">Rýchle doručenie</span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-[10px]">Bezpečný nákup</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
